import { SiInstagram, SiX, SiTiktok, SiOnlyfans, SiTelegram, SiWhatsapp, SiSnapchat, SiReddit, SiLinktree } from "react-icons/si";
import { Globe, Heart, Eye, EyeOff, ExternalLink } from "lucide-react";

export const SOCIAL_PLATFORMS = [
  { key: "instagram", url: "https://instagram.com/{}", label: "Instagram", ph: "@handle", Icon: SiInstagram },
  { key: "x", url: "https://x.com/{}", label: "X", ph: "@handle", Icon: SiX },
  { key: "tiktok", url: "https://tiktok.com/@{}", label: "TikTok", ph: "@handle", Icon: SiTiktok },
  { key: "onlyfans", url: "https://onlyfans.com/{}", label: "OnlyFans", ph: "username", Icon: SiOnlyfans },
  { key: "fansly", url: "https://fansly.com/{}", label: "Fansly", ph: "username", Icon: Heart },
  { key: "telegram", url: "https://t.me/{}", label: "Telegram", ph: "@username", Icon: SiTelegram },
  { key: "whatsapp", url: "https://wa.me/{}", label: "WhatsApp", ph: "+61 400 000 000", Icon: SiWhatsapp },
  { key: "snapchat", url: "https://snapchat.com/add/{}", label: "Snapchat", ph: "username", Icon: SiSnapchat },
  { key: "reddit", url: "https://reddit.com/user/{}", label: "Reddit", ph: "u/username", Icon: SiReddit },
  { key: "linktree", url: "https://linktr.ee/{}", label: "Linktree", ph: "username", Icon: SiLinktree },
  { key: "website", url: "https://{}", label: "Website", ph: "example.com", Icon: Globe },
];

const BY_KEY = Object.fromEntries(SOCIAL_PLATFORMS.map((p) => [p.key, p]));

export const socialUrl = (key, value) => {
  const tpl = BY_KEY[key]?.url;
  if (!value?.trim()) return key === "website" ? "" : new URL(tpl.replace("{}", "")).origin;
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  let h = key === "website" ? v : v.replace(/^@/, "").replace(/\s+/g, "");
  if (key === "reddit") h = h.replace(/^\/?u\//, "");
  if (key === "whatsapp") h = h.replace(/[^\d]/g, "");
  return tpl.replace("{}", encodeURI(h));
};

export const OpenLink = ({ k, value, testId, size = 11 }) => {
  const href = socialUrl(k, value);
  if (!href) return null;
  return (
    <a data-testid={testId} href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={(e) => e.stopPropagation()}
      title={value ? `Open ${BY_KEY[k].label} page` : `Open ${BY_KEY[k].label}`}
      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border border-white/10 text-text-muted hover:text-gold hover:border-gold/50 transition-colors">
      <ExternalLink size={size} /> {value ? "Web page" : "Site"}
    </a>
  );
};

export const SocialIcon = ({ k, size = 12, className = "text-gold" }) => {
  const Icon = BY_KEY[k]?.Icon || Globe;
  return <Icon size={size} className={`shrink-0 ${className}`} aria-hidden="true" />;
};

export const socialText = (links = {}) =>
  SOCIAL_PLATFORMS.filter((p) => links[p.key]).map((p) => `${p.label}: ${links[p.key]}`).join("\n");

const PublicToggle = ({ on, onClick, k }) => (
  <button type="button" data-testid={`public-toggle-${k}`} onClick={onClick} title={on ? "Shown on your profile" : "Hidden from your profile"}
    className={`flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border transition-colors ${on ? "border-gold/50 text-gold" : "border-white/10 text-text-muted"}`}>
    {on ? <Eye size={10} /> : <EyeOff size={10} />} {on ? "Public" : "Private"}
  </button>
);

export const SocialFields = ({ value = {}, onChange, prefix, publicKeys, onPublicChange, showLinks }) => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
    {SOCIAL_PLATFORMS.map((p) => {
      const pub = publicKeys?.includes(p.key);
      return (
        <label key={p.key} className="block">
          <span className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-text-muted"><SocialIcon k={p.key} /> {p.label}</span>
            {showLinks && <OpenLink k={p.key} value={value[p.key]} testId={`${prefix}-open-${p.key}`} />}
            {onPublicChange && value[p.key] && (
              <PublicToggle k={p.key} on={pub} onClick={(e) => { e.preventDefault(); onPublicChange(pub ? publicKeys.filter((x) => x !== p.key) : [...(publicKeys || []), p.key]); }} />
            )}
          </span>
          <input data-testid={`${prefix}-${p.key}`} value={value[p.key] || ""} placeholder={p.ph}
            onChange={(e) => onChange({ ...value, [p.key]: e.target.value })}
            className="w-full bg-transparent border-b border-white/15 py-2 text-sm placeholder:text-white/25 focus:border-gold outline-none transition-colors" />
        </label>
      );
    })}
  </div>
);

export const PublicSocialLinks = ({ links = [] }) => links.length ? (
  <div data-testid="profile-social-links" className="flex flex-wrap gap-2 mt-6">
    {links.map((l) => (
      <a key={l.key} data-testid={`profile-social-${l.key}`} href={l.url} target="_blank" rel="noopener noreferrer nofollow"
        className="flex items-center gap-2 text-xs px-3.5 py-2 rounded-full border border-gold/30 text-text-main hover:border-gold hover:bg-gold/10 transition-colors">
        <SocialIcon k={l.key} size={13} /> {l.display}
      </a>
    ))}
  </div>
) : null;
